import {
  deserialize,
  serialize,
} from '@/components/ui/TokenizedInput/serialization';
import {
  SelectTokenOption,
  Token,
  TokenDefinition,
} from '@/components/ui/TokenizedInput/types';
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { appLogger } from '@/config/logging';
import { Command as CommandPrimitive } from 'cmdk';
import { format } from 'date-fns';
import { RotateCcw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { first, isString, prop } from 'remeda';
import { Calendar as CalendarComponent } from '../calendar';
import { TokenElement } from './TokenElement';
import {
  getDefaultOperationsForType,
  operationDefinitions,
} from './operations';

const logger = appLogger.getChild('TokenizedInputComponent');

export interface TokenizedInputProps {
  value: string;
  onChange: (value: string) => void;
  tokenDefinitions: TokenDefinition[];
  className?: string;
  placeholder?: string;
  /** Called when clear button is clicked - clears to empty */
  onClear?: () => void;
  /** Called when reset button is clicked - resets to default filter */
  onReset?: () => void;
  /** Default filter value - used to determine if reset button should be shown */
  defaultFilter?: string;
  renderButtons?: () => React.ReactNode;
}

export interface TokenizedInputRef {
  focus: () => void;
}

export const TokenizedInput = forwardRef<
  TokenizedInputRef,
  TokenizedInputProps
>(function TokenizedInput(
  {
    value,
    placeholder = 'Type field name to filter...',
    onChange,
    className,
    tokenDefinitions,
    onClear,
    onReset,
    defaultFilter,
    renderButtons,
  }: TokenizedInputProps,
  ref,
) {
  if (typeof value !== 'string') {
    throw new Error(
      'TokenizedInput: value prop must be a string. Received: ' + typeof value,
    );
  }

  const parsed = useMemo(() => deserialize(value), [value]);
  const tokens = parsed.tokens;
  const remainderValue: string = parsed.partial
    ? `${parsed.partial.field} ${parsed.partial.operation}:${parsed.partial.value}`
    : parsed.remainder || '';

  const [focusedIndex, setFocusedIndex] = useState(tokens.length);
  const [hasFocus, _setHasFocus] = useState(false);
  const setHasFocus = useCallback((hasFocus: boolean) => {
    _setHasFocus(hasFocus);
  }, []);
  const [autocompleteVisible, setAutocompleteVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rightButtonsRef = useRef<HTMLDivElement>(null);
  const handleTokenKeyDown = (e: React.KeyboardEvent, tokenIndex: number) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        if (tokenIndex > 0) {
          setFocusedIndex(tokenIndex - 1);
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (tokenIndex < tokens.length) {
          setFocusedIndex(tokenIndex + 1);
        }
        break;
      case 'Backspace':
      case 'Delete':
        e.preventDefault();
        handleTokenRemove(tokenIndex);
        break;
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    const inputElement = inputRef.current?.querySelector(
      'input',
    ) as HTMLInputElement;
    if (!inputElement) return;

    switch (e.key) {
      case 'Escape':
        // Dismiss autocomplete dropdown without clearing text
        e.preventDefault();
        setAutocompleteVisible(false);
        break;
      case 'Enter':
        if (partialDefinition?.type === 'date') {
          logger.error('Date parsing not yet implemented', {
            partialDefinition,
            remainder: parsed.partial?.value,
          });
          // Ignore the Enter key for date fields until implementation is complete
          e.preventDefault();
          return;
        }
        break;
      case 'ArrowLeft':
        if (
          inputElement.selectionStart === 0 &&
          inputElement.selectionEnd === 0 &&
          tokens.length > 0
        ) {
          e.preventDefault();
          setFocusedIndex(tokens.length - 1);
        }
        break;
      case 'Backspace':
        if (
          inputElement.selectionStart === 0 &&
          inputElement.selectionEnd === 0 &&
          tokens.length > 0
        ) {
          e.preventDefault();
          const newTokens = tokens.slice(0, -1);
          const newState = {
            tokens: newTokens,
            partial: null,
            remainder: remainderValue,
          };
          onChange(serialize(newState));
          setFocusedIndex(newTokens.length);
        }
        break;
    }
  };

  const handleFieldSelect = (field: TokenDefinition) => {
    const operations =
      field.operations || getDefaultOperationsForType(field.type);
    const operation = first(operations) || 'is';
    const newState = {
      tokens,
      partial: null,
      remainder: `${field.key} ${operation}: `,
    };
    onChange(serialize(newState));
  };

  const handleOperationSelect = (operation: string) => {
    if (!parsed.partial) return;

    const updatedPartial = { ...parsed.partial, operation };
    const newState = { tokens, partial: updatedPartial, remainder: null };
    onChange(serialize(newState));
  };

  const handleValueSelect = useCallback(
    (selectedValue: string) => {
      if (!parsed.partial) return;

      const completedToken = { ...parsed.partial, value: selectedValue };
      const newState = {
        tokens: [...tokens, completedToken],
        partial: null,
        remainder: '',
      };
      onChange(serialize(newState));
      setFocusedIndex(newState.tokens.length);
    },
    [onChange, parsed.partial, tokens],
  );

  const handleTokenClick = (tokenIndex: number) => {
    setFocusedIndex(tokenIndex);
  };

  const handleTokenSave = (tokenIndex: number, updatedToken: Token) => {
    const newTokens = [...tokens];
    newTokens[tokenIndex] = updatedToken;
    const newState = {
      tokens: newTokens,
      partial: null,
      remainder: remainderValue,
    };
    onChange(serialize(newState));
  };

  const handleTokenRemove = (tokenIndex: number) => {
    const newTokens = tokens.filter((_, i) => i !== tokenIndex);
    const newState = {
      tokens: newTokens,
      partial: null,
      remainder: remainderValue,
    };
    onChange(serialize(newState));
  };

  useEffect(() => {
    if (hasFocus) {
      if (focusedIndex < tokens.length) {
        const tokenElements = document.querySelectorAll('[tabindex="0"]');
        const targetToken = Array.from(tokenElements).find(
          (el) => el.getAttribute('data-token-index') === String(focusedIndex),
        ) as HTMLElement;
        if (targetToken) {
          targetToken.focus();
        }
      } else {
        const commandInput = inputRef.current?.querySelector('input');
        commandInput?.focus();
      }
    }
  }, [focusedIndex, hasFocus, tokens.length]);

  const inputFocused = focusedIndex === tokens.length;
  const showAutocomplete =
    inputFocused &&
    hasFocus &&
    autocompleteVisible &&
    // Show when user has typed something OR when input is focused (even if empty)
    (parsed.remainder !== null ||
      // Show when actively editing a partial token
      parsed.partial !== null);
  const partialDefinition = parsed.partial
    ? tokenDefinitions.find((td) => td.key === parsed.partial?.field)
    : null;
  const requiresAutocomplete =
    isString(parsed.remainder) ||
    (parsed.partial && partialDefinition?.type === 'select');
  const requiresDateselection =
    parsed.partial && partialDefinition?.type === 'date';
  const requiresTextInput =
    parsed.partial && partialDefinition?.type === 'text';

  const [asyncOptions, setAsyncOptions] = useState<SelectTokenOption[] | null>(
    null,
  );
  const [asyncOptionsField, setAsyncOptionsField] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (parsed.partial?.field !== asyncOptionsField) {
      setAsyncOptions(null);
    }

    if (parsed.partial) {
      setAsyncOptionsField(parsed.partial.field);

      let isMounted = true;

      partialDefinition
        ?.fetchOptions?.(parsed.partial.value?.trim() || '')
        .then((options) => {
          if (isMounted) {
            setAsyncOptions(options);
          }
        })
        .catch((error) => {
          if (isMounted) {
            setAsyncOptions([]);
            logger.error('Error fetching options: {error}', {
              partial: parsed.partial,
              error,
            });
          }
        });

      return () => {
        isMounted = false;
      };
    }

    // Return empty cleanup function for when there's no partial
    return () => {};
  }, [parsed.partial, partialDefinition, asyncOptionsField]);

  const partialOptions = asyncOptions || partialDefinition?.options || null;

  const partialOperations =
    partialDefinition?.operations ||
    (partialDefinition
      ? getDefaultOperationsForType(partialDefinition.type)
      : ['is', 'is_not']);

  const handleContainerFocusIn = (e: React.FocusEvent) => {
    const target = e.target as HTMLElement;

    if (!target || !target.hasAttribute('data-roving-index')) {
      return;
    }

    setHasFocus(true);
    setAutocompleteVisible(true);

    const rovingIndex = target.getAttribute('data-roving-index');
    if (rovingIndex !== null) {
      const index = parseInt(rovingIndex, 10);
      setFocusedIndex(index);
    }
  };

  const handleContainerFocusOut = (e: React.FocusEvent) => {
    const target = e.target as HTMLElement;
    if (!target || !target.hasAttribute('data-roving-index')) {
      return;
    }

    if (
      !containerRef.current?.contains(e.relatedTarget as Node) ||
      rightButtonsRef.current?.contains(e.relatedTarget as Node)
    ) {
      setHasFocus(false);
      setAutocompleteVisible(false);
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      const commandInput = inputRef.current?.querySelector('input');
      commandInput?.focus();
    },
  }));

  return (
    <div
      ref={containerRef}
      className={cn(
        `border-input relative flex min-h-9 flex-wrap items-center gap-1.5 rounded-md
        border bg-transparent px-1 py-1 text-sm shadow-xs transition-[color,box-shadow]`,
        `focus-within:border-ring focus-within:ring-ring/20 focus-within:ring-2
        focus-within:ring-offset-0`,
        className,
      )}
      onFocusCapture={handleContainerFocusIn}
      onBlurCapture={handleContainerFocusOut}
    >
      {tokens.map((token, index) => {
        const tokenDefinition = tokenDefinitions.find(
          (td) => td.key === token.field,
        );

        if (!tokenDefinition) {
          logger.warn('Token definition not found for field: {field}', {
            field: token.field,
            availableFields: tokenDefinitions.map((td) => td.key),
          });
          return null;
        }

        return (
          <TokenElement
            tokenDefinition={tokenDefinition}
            // constraint an instanci to both index and key
            // otherwise it might cause an error when rendering when tokendefinition and loaded state dont match
            // i.e:
            //   - onRemove causes the tokendefinition on that index to change immediately
            //   - then the hide animation causes it to rerender with mismatching state
            //   - this potentially causes errors
            key={[tokenDefinition.key, index].join('-')}
            token={token}
            index={index}
            focused={focusedIndex === index}
            onKeyDown={handleTokenKeyDown}
            onClick={handleTokenClick}
            onTokenSave={handleTokenSave}
            onRemove={handleTokenRemove}
          />
        );
      })}
      {/* Input + buttons wrapper - keeps input and buttons together as a unit */}
      <div className="flex min-w-[130px] flex-1 items-center">
        <CommandPrimitive
          ref={inputRef}
          className="-overflow-hidden relative flex w-0 min-w-0 flex-1 border-none bg-transparent
            outline-none focus:ring-0"
          onKeyDown={handleInputKeyDown}
          // TODO: this is a result of rendering the partial token description in the input. this causes native search to not work. we should render a partial token component instead. leaving this for now as time is pressing.
          filter={(value, search, keywords) => {
            // When showing operations/values for partial tokens, don't filter
            if (parsed.partial) {
              if (partialDefinition?.type === 'text') {
                return 1;
              }
              // For select fields with fetchOptions, don't filter - server already filtered
              if (
                partialDefinition?.type === 'select' &&
                partialDefinition?.fetchOptions
              ) {
                return 1;
              }
              // For static select options, do client-side filtering
              return value
                .toLowerCase()
                .includes(parsed.partial.value.trim().toLowerCase())
                ? 1
                : 0;
            }
            // For fields, check value and keywords (includes label)
            const searchLower = search.trim().toLowerCase();
            if (value.toLowerCase().includes(searchLower)) {
              return 1;
            }
            // Check keywords (label, key, custom keywords)
            if (
              keywords?.some((kw) => kw.toLowerCase().includes(searchLower))
            ) {
              return 1;
            }
            return 0;
          }}
        >
          {/* TODO: reset selected index when command is seleced */}
          <CommandPrimitive.Input
            placeholder={placeholder}
            className="placeholder:text-muted-foreground w-0 min-w-12 flex-1 truncate border-none
              bg-transparent pl-1 outline-none focus:ring-0"
            value={remainderValue}
            tabIndex={inputFocused ? 0 : -1}
            data-roving-index={tokens.length}
            onValueChange={(newRemainder) => {
              const newState = {
                tokens,
                partial: null,
                remainder: newRemainder,
              };
              onChange(serialize(newState));
              // Show autocomplete when user types
              if (newRemainder.length > 0) {
                setAutocompleteVisible(true);
              }
            }}
          />

          {showAutocomplete && requiresAutocomplete && (
            <CommandList
              className="bg-popover absolute top-full left-0 z-50 mt-1 max-h-64 w-full overflow-auto
                border shadow-md"
            >
              {!partialDefinition ? (
                <>
                  {/* <CommandEmpty>No fields found.</CommandEmpty> */}
                  <CommandGroup heading="Fields">
                    {tokenDefinitions.map((def) => (
                      <CommandItem
                        key={def.key}
                        value={def.key}
                        keywords={[
                          def.label?.toLowerCase(),
                          def.key,
                          ...(def.keywords || []),
                        ].filter(isString)}
                        onSelect={() => handleFieldSelect(def)}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{def.label}</span>
                          <span className="text-muted-foreground text-xs">
                            {def.description}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              ) : (
                <>
                  <CommandEmpty>No operations or values found.</CommandEmpty>

                  {
                    <CommandGroup heading="Values">
                      {!partialOptions ? (
                        <div className="flex items-center justify-center py-2">
                          <span className="text-muted-foreground text-xs">
                            Loading...
                          </span>
                        </div>
                      ) : (
                        partialOptions
                          .filter((option) => {
                            const trimmedLowerValue = parsed.partial?.value
                              ?.trim()
                              .toLowerCase();
                            const keywordSearchResult: boolean | undefined =
                              trimmedLowerValue
                                ? partialDefinition
                                    .getOptionKeywords?.(option)
                                    ?.some((keyword) =>
                                      keyword
                                        .toLowerCase()
                                        .includes(trimmedLowerValue),
                                    )
                                : undefined;

                            // if keywordSearch was not possible, return true
                            return keywordSearchResult ?? true;
                          })
                          .map((option) => (
                            <CommandItem
                              key={(
                                partialDefinition.getOptionValue ||
                                prop('value')
                              )(option)}
                              value={(
                                partialDefinition.getOptionValue ||
                                prop('value')
                              )(option).toString()} // must be string ...
                              keywords={
                                partialDefinition.getOptionKeywords?.(option) ||
                                []
                              }
                              onSelect={() =>
                                handleValueSelect(
                                  (
                                    partialDefinition.getOptionValue ||
                                    prop('value')
                                  )(option),
                                )
                              }
                            >
                              {partialDefinition.renderItem ? (
                                partialDefinition.renderItem(
                                  option,
                                  partialDefinition,
                                  parsed.partial!,
                                )
                              ) : (
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {option.label}
                                  </span>
                                  <span className="text-muted-foreground text-xs">
                                    {option.description}
                                  </span>
                                </div>
                              )}
                            </CommandItem>
                          ))
                      )}
                    </CommandGroup>
                  }
                  {partialOperations.length > 1 && (
                    <CommandGroup heading="Operations">
                      {partialOperations.map((key) => {
                        const opDef = operationDefinitions.find(
                          (od) => od.key === key,
                        );
                        if (!opDef) return null;
                        return (
                          <CommandItem
                            key={opDef.key}
                            value={opDef.key}
                            keywords={[opDef.shortLabel, opDef.key].filter(
                              isString,
                            )}
                            onSelect={() => handleOperationSelect(key)}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{opDef.label}</span>
                              {/* <span className="text-xs text-muted-foreground">{opDef.}</span> */}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          )}
          {showAutocomplete && requiresDateselection && (
            <div className="bg-popover absolute top-full left-0 z-50 mt-1 w-full border shadow-md">
              <div className="flex">
                {/* Left column - Operation selection */}
                <div className="w-32 border-r p-2">
                  <div className="text-muted-foreground mb-2 text-xs font-medium">
                    Operation
                  </div>
                  {partialOperations.map((opKey) => {
                    const opDef = operationDefinitions.find(
                      (od) => od.key === opKey,
                    );
                    if (!opDef) return null;
                    const isSelected = parsed.partial?.operation === opKey;
                    return (
                      <button
                        key={opKey}
                        className={`hover:bg-accent w-full rounded px-2 py-1 text-left text-xs ${
                        isSelected ? 'bg-accent text-accent-foreground' : '' }`}
                        onClick={() => handleOperationSelect(opKey)}
                      >
                        {opDef.label}
                      </button>
                    );
                  })}
                </div>

                {/* Right column - Calendar */}
                <div className="flex-1 p-2">
                  <div className="text-muted-foreground mb-2 text-xs font-medium">
                    Date
                  </div>
                  <CalendarComponent
                    mode="single"
                    onSelect={(date) => {
                      if (date) {
                        const formattedDate = format(date, 'yyyy-MM-dd');
                        handleValueSelect(formattedDate);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {showAutocomplete && requiresTextInput && (
            <CommandList
              className="bg-popover absolute top-full left-0 z-50 mt-1 max-h-64 w-full overflow-auto
                border shadow-md"
            >
              <CommandEmpty>No operations found.</CommandEmpty>
              <CommandGroup heading="Complete Token">
                {partialOperations.map((key) => {
                  const opDef = operationDefinitions.find(
                    (od) => od.key === key,
                  );
                  if (!opDef) return null;
                  return (
                    <CommandItem
                      key={opDef.key}
                      value={opDef.key}
                      keywords={[opDef.shortLabel, opDef.key].filter(isString)}
                      onSelect={() => {
                        if (parsed.partial) {
                          const completedToken = {
                            ...parsed.partial,
                            operation: key,
                            value: parsed.partial.value.trim(),
                          };
                          const newState = {
                            tokens: [...tokens, completedToken],
                            partial: null,
                            remainder: '',
                          };
                          onChange(serialize(newState));
                          setFocusedIndex(newState.tokens.length);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          <span className="">{partialDefinition?.label}</span>{' '}
                          <span className="italic">{opDef.label}</span>{' '}
                          <span className="font-mono">
                            "{parsed.partial?.value.trim()}"
                          </span>
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          )}
        </CommandPrimitive>

        {/* Right-aligned buttons */}
        <div ref={rightButtonsRef} className="flex shrink-0 items-center gap-1">
          {renderButtons && renderButtons()}
          {/* Reset to default button - show when filter differs from default */}
          {onReset &&
            defaultFilter !== undefined &&
            value !== defaultFilter && (
              <button
                onClick={onReset}
                className="hover:bg-accent hover:text-accent-foreground flex h-6 w-6 items-center
                  justify-center rounded-sm transition-colors"
                title="Reset to default filter"
                tabIndex={-1}
              >
                <RotateCcw className="text-muted-foreground h-3.5 w-3.5" />
              </button>
            )}
          {/* Clear button - show when there's content */}
          {onClear && (tokens.length > 0 || !!remainderValue.length) && (
            <button
              onClick={onClear}
              className="hover:bg-accent hover:text-accent-foreground flex h-6 w-6 items-center
                justify-center rounded-sm transition-colors"
              title="Clear all filters"
              tabIndex={-1}
            >
              <X className="text-muted-foreground h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
